import useServerInfo from "@hooks/useServerInfo";
import { useExperiment, useStatsigClient, useStatsigUser } from "@statsig/react-bindings";
import { NotificationPermissionState } from "@types";
import { getStoredCredential } from "@utils/credentials";
import logger from "@utils/logger";
import { getReferralCode } from "@utils/referral";
import {
  GameModel,
  LoginGrantType,
  MemberModel,
  MemberSettingAuthMode,
  WalletTxMethod,
  WithdrawRequest,
  WithdrawResponse,
} from "kz-ui-sdk";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { EVENTS, EXPERIMENTS, EXPERIMENT_VALUES, EventName } from "./constants";
import {
  AuthSource,
  CashbackMiniPromptShownMetadata,
  ClaimCashbackMiniPromptMetadata,
  ClickMiniBannerMetadata,
  ClickPromotionItemMetadata,
  ClickPromotionPageMetadata,
  CloseDepositIframeMetadata,
  CloseDepositNativeMetadata,
  DepositCreateResultOptions,
  DepositGroupsEmptyMetadata,
  DepositIframeCloseReason,
  DepositMethodsEmptyMetadata,
  DepositNativeCloseReason,
  DepositSource,
  DepositStep,
  EventMetadata,
  LanguageChangeSource,
  NotificationAction,
  NotificationBrowserPromptResponseMetadata,
  NotificationInAppPromptResponseMetadata,
  NotificationInAppPromptShownMetadata,
  NotificationInAppSource,
  NotificationSubscriptionStatusMetadata,
  OTPFailedAttemptMetadata,
  OTPMethod,
  OTPSource,
  RegistrationFormFocusMetadata,
  RegistrationMethod,
  RegistrationStep,
} from "./types";
import {
  DepositFunnelTracker,
  RegistrationFunnelTracker,
  buildUserProperties,
  hasSeenAndMarkCloseDeposit,
  hasSeenAndMarkDeposit,
  hasSeenAndMarkRegistrationFormFocus,
  isExperimentRecognized,
  isValidEvent,
} from "./utils";

/**
 * Unified Statsig tracking hook - replaces all specialized hooks
 * Simple, maintainable, and scalable approach
 */
export function useStatsigTracking() {
  const { client } = useStatsigClient();
  const { updateUserAsync, user } = useStatsigUser();
  const serverInfo = useServerInfo();

  const pendingEvents = useRef<Array<{ eventName: string; value?: number; metadata?: Record<string, any> }>>([]);

  // Main tracking function - simple and flexible
  const track = useCallback(
    (eventName: EventName | string, metadata?: EventMetadata, value?: number) => {
      if (!client) {
        logger._console.warn("Statsig client not available, queueing event");
        // Store event for later if client not ready
        pendingEvents.current.push({ eventName, value, metadata });
        return;
      }

      // Validate event name (dev warning only)
      if (!isValidEvent(eventName) && !eventName.startsWith("act:") && !eventName.startsWith("evt:")) {
        logger._console.warn(`Event name "${eventName}" doesn't follow naming convention (act: or evt:)`);
      }

      try {
        // Clean, simple event logging
        client.logEvent(eventName, value, metadata || {});

        // Log successful event for debugging
        logger._console.log(`[Statsig] ${eventName}`, { value, metadata });
      } catch (error) {
        logger._console.error("Failed to log Statsig event:", error);
        // Store failed event for retry
        pendingEvents.current.push({ eventName, value, metadata });
      }
    },
    [client],
  );

  // Process any pending events when client becomes available
  useEffect(() => {
    if (client && pendingEvents.current.length > 0) {
      const eventsToProcess = [...pendingEvents.current];
      pendingEvents.current = [];

      eventsToProcess.forEach(({ eventName, value, metadata }) => {
        track(eventName, metadata, value);
      });
    }
  }, [client, track]);

  // Flush pending events manually (useful for critical moments)
  const flushEvents = useCallback(() => {
    if (client && pendingEvents.current.length > 0) {
      const eventsToProcess = [...pendingEvents.current];
      pendingEvents.current = [];

      eventsToProcess.forEach(({ eventName, value, metadata }) => {
        track(eventName, metadata, value);
      });
    }
  }, [client, track]);

  // ===== SPECIALIZED TRACKING FUNCTIONS =====
  // These functions handle metadata construction internally, reducing component coupling

  // Registration Flow Tracking
  const trackRegisterClick = useCallback(
    (source: AuthSource) => {
      const referralCode = getReferralCode();
      const tracker = RegistrationFunnelTracker.getInstance();
      tracker.startRegistration();
      tracker.addStep(RegistrationStep.CLICK_REGISTER);

      track(EVENTS.CLICK_REGISTER, {
        source,
        step: RegistrationStep.CLICK_REGISTER,
        referralCode,
        hasReferralCode: !!referralCode,
      });
    },
    [track],
  );

  const trackRegisterFormFocus = useCallback(
    (method: RegistrationMethod) => {
      if (hasSeenAndMarkRegistrationFormFocus(method)) return;
      const metaData: RegistrationFormFocusMetadata = {
        method,
        pathname: window.location.pathname,
        fromPathname: window.history?.state?.usr?.from?.pathname ?? undefined,
        search: window.location.search ?? undefined,
      };
      track(EVENTS.REGISTRATION_FORM_FOCUS, metaData);
    },
    [track],
  );

  const trackRegisterFormSubmit = useCallback(
    (
      method: RegistrationMethod,
      options?: {
        timeSpentOnForm?: number;
        retryCount?: number;
      },
    ) => {
      const referralCode = getReferralCode();
      const tracker = RegistrationFunnelTracker.getInstance();
      tracker.addStep(RegistrationStep.FORM_SUBMIT);
      const retryCount = tracker.incrementAttempt("register_form_submit");

      track(EVENTS.REGISTER_FORM_SUBMIT, {
        method,
        step: RegistrationStep.FORM_SUBMIT,
        timeSpentOnForm: options?.timeSpentOnForm,
        retryCount: options?.retryCount || retryCount,
        referralCode,
        hasReferralCode: !!referralCode,
      });
    },
    [track],
  );

  const trackOTPSent = useCallback(
    (
      method: string,
      options?: {
        deliveryTime?: number;
        retryCount?: number;
        source?: OTPSource;
      },
    ) => {
      const tracker = RegistrationFunnelTracker.getInstance();
      // Only track registration funnel steps/attempts when source is register (default)
      const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
      if (isRegisterSource) {
        tracker.addStep(RegistrationStep.OTP_SENT);
      }
      const computedRetryCount = isRegisterSource ? tracker.incrementAttempt("otp_sent") : undefined;

      track(EVENTS.OTP_REQUEST_RESULT, {
        method,
        step: RegistrationStep.OTP_SENT,
        deliveryTime: options?.deliveryTime,
        retryCount: options?.retryCount || computedRetryCount,
        source: options?.source,
        success: true,
      });
    },
    [track],
  );

  const trackOTPVerified = useCallback(
    (
      method: string,
      options?: {
        attemptsCount?: number;
        timeToVerify?: number;
        source?: OTPSource;
      },
    ) => {
      const tracker = RegistrationFunnelTracker.getInstance();
      const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
      if (isRegisterSource) {
        tracker.addStep(RegistrationStep.OTP_VERIFY);
      }

      track(EVENTS.OTP_VERIFY_RESULT, {
        method,
        step: RegistrationStep.OTP_VERIFY,
        attemptsCount: options?.attemptsCount || 1,
        timeToVerify: options?.timeToVerify,
        source: options?.source,
        success: true,
      });
    },
    [track],
  );

  const trackOTPRequestFailed = useCallback(
    (error: any, options?: { source?: OTPSource }) => {
      const otpTracker = RegistrationFunnelTracker.getInstance();
      const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;

      const metadata: OTPFailedAttemptMetadata = {
        method: OTPMethod.SMS,
        step: RegistrationStep.OTP_SENT,
        error,
        attemptsCount: isRegisterSource ? otpTracker.incrementAttempt("otp_request") : 1,
        source: options?.source,
      };
      track(EVENTS.OTP_REQUEST_RESULT, { ...metadata, success: false });
    },
    [track],
  );

  const trackOTPFailedAttempt = useCallback(
    (error: any, options?: { source?: OTPSource }) => {
      // Track OTP verification failure with analytics data
      const failTracker = RegistrationFunnelTracker.getInstance();
      const isRegisterSource = (options?.source ?? OTPSource.REGISTER) === OTPSource.REGISTER;
      const failMetadata = {
        method: OTPMethod.SMS,
        step: RegistrationStep.OTP_VERIFY,
        attemptsCount: isRegisterSource ? failTracker.incrementAttempt("otp_verify_fail") : 1,
        error,
        source: options?.source,
        success: false,
      };

      track(EVENTS.OTP_VERIFY_RESULT, failMetadata);
    },
    [track],
  );

  const trackOTPRequest = useCallback(
    (
      method: string,
      options?: {
        source?: OTPSource;
      },
    ) => {
      track(EVENTS.OTP_REQUEST, {
        method,
        source: options?.source,
      });
    },
    [track],
  );

  const trackRegistrationComplete = useCallback(
    async (
      method: RegistrationMethod,
      options?: {
        member?: MemberModel;
      },
    ) => {
      try {
        const referralCode = getReferralCode();
        const tracker = RegistrationFunnelTracker.getInstance();
        tracker.addStep(RegistrationStep.REGISTRATION_COMPLETE);

        if (options?.member) {
          const userId = `${serverInfo?.prefix || ""}${options?.member?.apiIdentifier}`;
          if (user?.userID !== userId) {
            const userProperties = buildUserProperties(options?.member, serverInfo?.prefix || "");
            const res = await updateUserAsync({
              ...userProperties,
            });
            logger._console.log("[Statsig] updateUserAsync result", res, userProperties);
          }
        }

        track(EVENTS.REGISTRATION_RESULT, {
          method,
          step: RegistrationStep.REGISTRATION_COMPLETE,
          hasReferralCode: !!referralCode,
          referralCode,
          totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
          stepsCompleted: tracker.getCompletedSteps(),
          success: true,
        });
      } catch (error) {
        logger._console.error("Failed to update Statsig user:", error);
      }
    },
    [track, updateUserAsync, serverInfo, user],
  );

  const trackRegistrationFailed = useCallback(
    (method: RegistrationMethod, error: any) => {
      const referralCode = getReferralCode();

      const tracker = RegistrationFunnelTracker.getInstance();
      track(EVENTS.REGISTRATION_RESULT, {
        method,
        hasReferralCode: !!referralCode,
        referralCode,
        success: false,
        error,
        totalTimeToComplete: Math.floor(tracker.getTotalTime() / 1000),
      });
    },
    [track],
  );

  // General/Login Tracking

  const trackLoginClick = useCallback(
    (source: AuthSource) => {
      track(EVENTS.CLICK_LOGIN, {
        source: source,
      });
    },
    [track],
  );

  // Auth Click Tracking, check internal credential to determine login or register
  const trackAuthClick = useCallback(
    (source: AuthSource, requireRegister: boolean = true) => {
      const hasSavedPhoneNumber = getStoredCredential()?.clientId;
      if (hasSavedPhoneNumber) {
        trackLoginClick(source);
      } else {
        if (requireRegister) {
          trackRegisterClick(source);
        } else {
          trackLoginClick(source);
        }
      }
    },
    [trackLoginClick, trackRegisterClick],
  );

  const trackLogin = useCallback(
    (
      authMode: MemberSettingAuthMode,
      options?: {
        grantType?: LoginGrantType;
      },
    ) => {
      track(EVENTS.LOGIN, {
        authMode,
        grantType: options?.grantType?.toString(),
      });
    },
    [track],
  );

  const trackLoginFormSubmit = useCallback(
    (
      authMode: MemberSettingAuthMode,
      grantType: LoginGrantType,
      options?: {
        timeSpentOnForm?: number;
      },
    ) => {
      track(EVENTS.LOGIN_FORM_SUBMIT, {
        authMode,
        grantType: grantType?.toString(),
        timeSpentOnForm: options?.timeSpentOnForm,
      });
    },
    [track],
  );

  const trackLoginFailed = useCallback(
    (
      error: string | undefined,
      options?: {
        authMode?: MemberSettingAuthMode;
        grantType?: LoginGrantType;
      },
    ) => {
      track(EVENTS.LOGIN_FAILED, {
        authMode: options?.authMode,
        grantType: options?.grantType?.toString(),
        error,
      });
    },
    [track],
  );

  // Deposit Flow Tracking
  const trackDepositClick = useCallback(
    (
      source: DepositSource | null | undefined,
      options?: {
        accountBalance?: number;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.startDeposit();
      tracker.addStep(DepositStep.CLICK_DEPOSIT);

      track(EVENTS.CLICK_DEPOSIT, {
        source: source ?? null,
        previousPage: window.location.pathname,
        accountBalance: options?.accountBalance,
      });
    },
    [track],
  );

  const trackDepositMethodGroup = useCallback(
    (groupId: string, groupName: string, source?: DepositSource | null) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.SELECT_GROUP);
      tracker.setSelectedData("groupId", groupId);
      tracker.setSelectedData("groupName", groupName);

      track(EVENTS.SELECT_DEPOSIT_METHOD_GROUP, {
        source: source ?? null,
        step: DepositStep.SELECT_GROUP,
        groupId,
        groupName,
      });
    },
    [track],
  );

  const trackDepositMethodIndividual = useCallback(
    (
      methodId: string,
      methodName: string,
      options?: {
        source?: DepositSource;
        methodKey?: string;
        groupId?: string;
        isRecommended?: boolean;
        fees?: number;
        processingTime?: string;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.SELECT_METHOD);
      tracker.setSelectedData("methodId", methodId);
      tracker.setSelectedData("methodName", methodName);

      track(EVENTS.SELECT_DEPOSIT_METHOD_INDIVIDUAL, {
        source: options?.source,
        step: DepositStep.SELECT_METHOD,
        methodId,
        methodName,
        methodKey: options?.methodKey,
        groupId: options?.groupId,
        isRecommended: options?.isRecommended,
        fees: options?.fees,
        processingTime: options?.processingTime,
      });
    },
    [track],
  );

  const trackAutoFastTriggered = useCallback(
    (
      autoSelectedMethod: string,
      options?: {
        source?: DepositSource;
        originalMethod?: string;
        reason?: string;
        timeSaved?: string;
      },
    ) => {
      track(EVENTS.AUTO_FAST_TRIGGERED, {
        source: options?.source,
        step: DepositStep.SELECT_METHOD,
        originalMethod: options?.originalMethod,
        autoSelectedMethod,
        reason: options?.reason || "auto_selection",
        timeSaved: options?.timeSaved,
      });
    },
    [track],
  );

  const trackDepositAmountSelect = useCallback(
    (
      amount: number,
      options?: {
        source?: DepositSource;
        selectedMethod?: string;
        isQuickAmount?: boolean;
        bonusOffered?: number;
        currency?: string;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.SELECT_AMOUNT);
      tracker.setSelectedData("amount", amount);

      track(EVENTS.SELECT_DEPOSIT_AMOUNT, {
        source: options?.source,
        step: DepositStep.SELECT_AMOUNT,
        amount,
        selectedMethod: options?.selectedMethod,
        bonusOffered: options?.bonusOffered,
        currency: options?.currency,
      });
    },
    [track],
  );

  const trackTopUpFailed = useCallback(
    (error: any, options?: DepositCreateResultOptions) => {
      track(EVENTS.DEPOSIT_CREATE_RESULT, {
        source: options?.source,
        currency: options?.currency,
        error,
        success: false,
        methodId: options?.methodId,
        methodName: options?.methodName,
        orderRef: options?.orderRef,
        depositGroupId: options?.depositGroupId,
        depositGroupName: options?.depositGroupName,
        methodKey: options?.methodKey,
        amount: options?.amount,
        providerKey: options?.providerKey,
        paymentMethod: options?.paymentMethod,
        payDataType: options?.payDataType,
      });
    },
    [track],
  );

  const trackTopUpSubmit = useCallback(
    (
      amount: number,
      methodId: string,
      methodName: string,
      options?: {
        source?: DepositSource;
        currency?: string;
        estimatedProcessingTime?: string;
      },
    ) => {
      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.SUBMIT_TOP_UP);

      track(EVENTS.TOP_UP_SUBMIT, {
        source: options?.source,
        step: DepositStep.SUBMIT_TOP_UP,
        amount,
        methodId,
        methodName,
        currency: options?.currency,
        estimatedProcessingTime: options?.estimatedProcessingTime,
      });
    },
    [track],
  );

  const trackDepositSuccess = useCallback(
    (
      amount: number,
      transactionId: string,
      options?: {
        source?: DepositSource;
        method?: string;
        currency?: string;
        actualProcessingTime?: number;
        methodId?: string;
        providerKey?: string;
      },
    ) => {
      // De-duplicate across modules/sources (WS vs polling) using transactionId/orderRef
      if (hasSeenAndMarkDeposit(transactionId)) {
        logger._console.log("[Statsig] Skipping duplicate deposit success track for", transactionId);
        return;
      }

      const tracker = DepositFunnelTracker.getInstance();
      tracker.addStep(DepositStep.DEPOSIT_COMPLETE);

      track(EVENTS.DEPOSIT_SUCCESS, {
        source: options?.source,
        step: DepositStep.DEPOSIT_COMPLETE,
        amount,
        methodId: options?.methodId,
        method: options?.method,
        orderRef: transactionId,
        providerKey: options?.providerKey,
        currency: options?.currency,
        actualProcessingTime: options?.actualProcessingTime,
        totalDepositTime: Math.floor(tracker.getTotalTime() / 1000),
      });
    },
    [track],
  );

  const trackDepositFailed = useCallback(
    (
      amount: number,
      methodId: string,
      error: any,
      options?: {
        source?: DepositSource;
        methodName?: string;
        currency?: string;
        retryAttempt?: number;
        failurePoint?: string;
      },
    ) => {
      track(EVENTS.DEPOSIT_FAILED, {
        source: options?.source,
        step: DepositStep.SUBMIT_DEPOSIT, // Usually fails at submit
        amount,
        methodId,
        methodName: options?.methodName,
        currency: options?.currency,
        error,
        retryAttempt: options?.retryAttempt || 1,
        failurePoint: options?.failurePoint,
      });
    },
    [track],
  );

  const trackCloseDepositIframe = useCallback(
    (metadata: CloseDepositIframeMetadata) => {
      if (
        metadata.reason === DepositIframeCloseReason.DEPOSIT_SUCCESS &&
        hasSeenAndMarkCloseDeposit(metadata.orderRef, metadata.reason)
      ) {
        return;
      }
      track(EVENTS.CLOSE_DEPOSIT_IFRAME, {
        reason: metadata.reason,
        orderRef: metadata.orderRef,
      });
    },
    [track],
  );

  const trackOpenDepositIframe = useCallback(
    (metadata: DepositCreateResultOptions) => {
      track(EVENTS.OPEN_DEPOSIT_IFRAME, {
        orderRef: metadata.orderRef,
        methodId: metadata.methodId,
        methodName: metadata.methodName,
        methodKey: metadata.methodKey,
        providerKey: metadata.providerKey,
        depositGroupId: metadata.depositGroupId,
        depositGroupName: metadata.depositGroupName,
        paymentMethod: metadata.paymentMethod,
        amount: metadata.amount,
        payDataType: metadata.payDataType,
      });
    },
    [track],
  );

  const trackDepositCreateSuccess = useCallback(
    (amount: number, options: DepositCreateResultOptions) => {
      track(EVENTS.DEPOSIT_CREATE_RESULT, {
        source: options?.source,
        currency: options?.currency,
        success: true,
        amount,
        methodId: options?.methodId,
        methodName: options?.methodName,
        orderRef: options?.orderRef,
        depositGroupId: options?.depositGroupId,
        depositGroupName: options?.depositGroupName,
        methodKey: options?.methodKey,
        providerKey: options?.providerKey,
        paymentMethod: options?.paymentMethod,
        payDataType: options?.payDataType,
      });
    },
    [track],
  );

  const trackCloseDepositCustom = useCallback(
    (metadata: CloseDepositNativeMetadata) => {
      if (
        metadata.reason === DepositNativeCloseReason.DEPOSIT_SUCCESS &&
        hasSeenAndMarkCloseDeposit(metadata.orderRef, metadata.reason)
      ) {
        return;
      }
      track(EVENTS.CLOSE_DEPOSIT_CUSTOM, {
        orderRef: metadata.orderRef,
        reason: metadata.reason,
      });
    },
    [track],
  );

  const trackOpenDepositCustom = useCallback(
    (metadata: DepositCreateResultOptions) => {
      track(EVENTS.OPEN_DEPOSIT_CUSTOM, {
        orderRef: metadata.orderRef,
        methodId: metadata.methodId,
        methodName: metadata.methodName,
        methodKey: metadata.methodKey,
        providerKey: metadata.providerKey,
        depositGroupId: metadata.depositGroupId,
        depositGroupName: metadata.depositGroupName,
        paymentMethod: metadata.paymentMethod,
        amount: metadata.amount,
        payDataType: metadata.payDataType,
      });
    },
    [track],
  );

  const trackGameOpened = useCallback(
    (game: Partial<GameModel>, options?: { source?: string; searchTerm?: string }) => {
      track(EVENTS.GAME_OPENED, {
        gameId: game?.id,
        gameName: game?.name,
        gameProvider: game?.providerRef,
        gameProviderId: game?.gameProviderId,
        gameType: game?.type,
        gameCategory: game?.category,
        gameDescription: game?.description,
        gameRank: game?.rank,
        gameStatus: game?.status,
        gamePrimaryImageUrl: game?.primaryImage?.url,
        isMaintenance: game?.isMaintenance,
        source: options?.source,
        searchTerm: options?.searchTerm,
      });
    },
    [track],
  );

  const trackGameOpenTriggered = useCallback(
    (game: Partial<GameModel>, options?: { source?: string; searchTerm?: string }) => {
      track(EVENTS.GAME_OPEN_TRIGGERED, {
        gameId: game?.id,
        gameName: game?.name,
        gameProvider: game?.providerRef,
        gameProviderId: game?.gameProviderId,
        gameType: game?.type,
        gameCategory: game?.category,
        gameDescription: game?.description,
        gameRank: game?.rank,
        gameStatus: game?.status,
        gamePrimaryImageUrl: game?.primaryImage?.url,
        isMaintenance: game?.isMaintenance,
        source: options?.source,
        searchTerm: options?.searchTerm,
      });
    },
    [track],
  );

  const trackGameSearch = useCallback(
    (searchTerm: string | undefined, options?: { resultCount?: number }) => {
      track(EVENTS.GAME_SEARCH, {
        searchTerm,
        resultCount: options?.resultCount,
      });
    },
    [track],
  );

  const trackGameSearchSelect = useCallback(
    (game: Partial<GameModel>, options?: { source?: string; searchTerm?: string }) => {
      track(EVENTS.GAME_SEARCH_SELECT, {
        game,
        source: options?.source,
        searchTerm: options?.searchTerm,
      });
    },
    [track],
  );

  const trackGameClosed = useCallback(
    (game: Partial<GameModel>) => {
      track(EVENTS.GAME_CLOSED, {
        gameId: game?.id,
        gameName: game?.name,
        gameProvider: game?.providerRef,
        gameProviderId: game?.gameProviderId,
        gameType: game?.type,
        gameCategory: game?.category,
        gameDescription: game?.description,
        gameRank: game?.rank,
        gameStatus: game?.status,
        gamePrimaryImageUrl: game?.primaryImage?.url,
        isMaintenance: game?.isMaintenance,
      });
    },
    [track],
  );

  const trackGameError = useCallback(
    (error: any) => {
      track(EVENTS.GAME_ERROR, {
        error,
      });
    },
    [track],
  );

  const trackSessionTimeout = useCallback(() => {
    track(EVENTS.SESSION_TIMEOUT, {});
  }, [track]);

  const trackPromoClaimAttempt = useCallback(
    (promoType: WalletTxMethod | "refer-ball") => {
      track(EVENTS.PROMO_CLAIM_ATTEMPT, {
        promoType,
      });
    },
    [track],
  );

  const trackPromoClaimSuccess = useCallback(
    (promoType: WalletTxMethod | "refer-ball") => {
      track(EVENTS.PROMO_CLAIM_SUCCESS, {
        promoType,
      });
    },
    [track],
  );

  const trackPromoRewardApplied = useCallback(
    (promoId: string) => {
      track(EVENTS.PROMO_REWARD_APPLIED, {
        promoId,
      });
    },
    [track],
  );

  const trackPromoBonusExpired = useCallback(
    (promoId: string) => {
      track(EVENTS.PROMO_BONUS_EXPIRED, {
        promoId,
      });
    },
    [track],
  );

  const trackClickMiniBanner = useCallback(
    (metadata: ClickMiniBannerMetadata) => {
      track(EVENTS.CLICK_MINI_BANNER, metadata);
    },
    [track],
  );

  const trackClickPromotionPage = useCallback(
    (metadata: ClickPromotionPageMetadata) => {
      track(EVENTS.CLICK_PROMOTION_PAGE, metadata);
    },
    [track],
  );

  const trackClickPromotionItem = useCallback(
    (metadata: ClickPromotionItemMetadata) => {
      track(EVENTS.CLICK_PROMOTION_ITEM, metadata);
    },
    [track],
  );

  const trackCashbackMiniPromptShown = useCallback(
    (metadata: CashbackMiniPromptShownMetadata) => {
      track(EVENTS.CASHBACK_MINI_PROMPT_SHOWN, metadata);
    },
    [track],
  );

  const trackClaimCashbackMiniPrompt = useCallback(
    (metadata: ClaimCashbackMiniPromptMetadata) => {
      track(EVENTS.CLAIM_CASHBACK_MINI_PROMPT, metadata);
    },
    [track],
  );

  // Deposit Empty States Tracking
  const trackDepositGroupsEmpty = useCallback(
    (metadata: DepositGroupsEmptyMetadata) => {
      track(EVENTS.DEPOSIT_GROUPS_EMPTY, metadata);
    },
    [track],
  );

  const trackDepositMethodsEmpty = useCallback(
    (metadata: DepositMethodsEmptyMetadata) => {
      track(EVENTS.DEPOSIT_METHODS_EMPTY, metadata);
    },
    [track],
  );

  const trackWithdrawalClick = useCallback(() => {
    track(EVENTS.CLICK_WITHDRAWAL, {});
  }, [track]);

  const trackWithdrawalSubmit = useCallback(
    (payload: WithdrawRequest) => {
      track(EVENTS.WITHDRAWAL_SUBMIT, {
        payload,
      });
    },
    [track],
  );

  const trackWithdrawalSuccess = useCallback(
    (response: WithdrawResponse) => {
      track(EVENTS.WITHDRAWAL_SUCCESS, {
        response,
      });
    },
    [track],
  );
  const trackWithdrawalFailed = useCallback(
    (error: any) => {
      track(EVENTS.WITHDRAWAL_FAILED, {
        error,
      });
    },
    [track],
  );

  // Notifications tracking
  const trackNotificationInAppPromptShown = useCallback(
    (source: NotificationInAppSource = NotificationInAppSource.PRIZES_PAGE) => {
      const metadata: NotificationInAppPromptShownMetadata = {
        source,
      };
      track(EVENTS.NOTIFICATION_INAPP_PROMPT_SHOWN, metadata);
    },
    [track],
  );

  const trackNotificationInAppPromptResponse = useCallback(
    (action: NotificationAction) => {
      const metadata: NotificationInAppPromptResponseMetadata = {
        action,
      };
      track(EVENTS.NOTIFICATION_INAPP_PROMPT_RESPONSE, metadata);
    },
    [track],
  );

  const trackNotificationBrowserPromptShown = useCallback(() => {
    track(EVENTS.NOTIFICATION_BROWSER_PROMPT_SHOWN, {});
  }, [track]);

  const trackNotificationBrowserPromptResponse = useCallback(
    (action: NotificationAction) => {
      const metadata: NotificationBrowserPromptResponseMetadata = {
        action,
      };
      track(EVENTS.NOTIFICATION_BROWSER_PROMPT_RESPONSE, metadata);
    },
    [track],
  );

  const trackNotificationSubscriptionStatus = useCallback(
    (status: NotificationPermissionState) => {
      const metadata: NotificationSubscriptionStatusMetadata = {
        status,
      };
      track(EVENTS.NOTIFICATION_SUBSCRIPTION_STATUS, metadata);
    },
    [track],
  );

  const trackChangeLanguage = useCallback(
    (from: string, to: string, source: LanguageChangeSource) => {
      if (from === to) return;
      track(EVENTS.CHANGE_LANGUAGE, { from, to, source });
    },
    [track],
  );

  return {
    // Core tracking
    track,
    flushEvents,
    isReady: !!client,

    // Registration tracking
    trackRegisterFormFocus,
    trackRegisterClick,
    trackRegisterFormSubmit,
    trackOTPSent,
    trackOTPVerified,
    trackRegistrationComplete,
    trackRegistrationFailed,
    trackOTPRequestFailed,
    trackOTPFailedAttempt,
    trackOTPRequest,

    // General
    trackLoginClick,
    trackLogin,
    trackLoginFormSubmit,
    trackLoginFailed,
    trackAuthClick,

    // Deposit tracking
    trackDepositClick,
    trackDepositMethodGroup,
    trackDepositMethodIndividual,
    trackAutoFastTriggered,
    trackDepositAmountSelect,
    trackTopUpSubmit,
    trackDepositSuccess,
    trackDepositFailed,
    trackTopUpFailed,
    trackDepositCreateSuccess,
    trackCloseDepositIframe,
    trackOpenDepositIframe,
    trackCloseDepositCustom,
    trackOpenDepositCustom,

    // Gameplay tracking
    trackGameOpenTriggered,
    trackGameSearch,
    trackGameOpened,
    trackGameClosed,
    trackGameError,
    trackSessionTimeout,
    trackGameSearchSelect,

    // Promotions tracking
    trackPromoClaimAttempt,
    trackPromoClaimSuccess,
    trackPromoRewardApplied,
    trackPromoBonusExpired,
    trackClickMiniBanner,
    trackClickPromotionPage,
    trackClickPromotionItem,
    trackCashbackMiniPromptShown,
    trackClaimCashbackMiniPrompt,
    trackDepositGroupsEmpty,
    trackDepositMethodsEmpty,

    // Withdrawals tracking
    trackWithdrawalClick,
    trackWithdrawalSubmit,
    trackWithdrawalSuccess,
    trackWithdrawalFailed,

    // Notifications tracking
    trackNotificationInAppPromptShown,
    trackNotificationInAppPromptResponse,
    trackNotificationBrowserPromptShown,
    trackNotificationBrowserPromptResponse,
    trackNotificationSubscriptionStatus,

    // Language
    trackChangeLanguage,
  };
}

/**
 * Hook to check Statsig status
 */
export function useStatsigStatus() {
  const { client } = useStatsigClient();

  return {
    isReady: !!client,
    hasClientKey: !!import.meta.env.VITE_STATSIG_CLIENT_KEY,
  };
}

/**
 * Hook for Prizes to Promotion wording experiment
 */
export function usePrizesWordingExperiment() {
  const experiment = useExperiment(EXPERIMENTS.prizesToPromotion.name);

  // Get boolean parameter from Statsig (true = show promotion, false = show prizes)
  const showPromotion = experiment.get(EXPERIMENTS.prizesToPromotion.parameters.wording, false);
  const prizesLabelKey = showPromotion ? EXPERIMENT_VALUES.PROMOTION : EXPERIMENT_VALUES.PRIZES;
  const isPromotionVariant = showPromotion;

  return {
    prizesLabelKey,
    isPromotionVariant,
    experimentName: EXPERIMENTS.prizesToPromotion.name,
  };
}

export function useForgotButtonPosition() {
  const { client, isLoading, getExperiment } = useStatsigClient();

  const showProminentButton = useMemo(() => {
    if (isLoading || !getExperiment || !client) {
      return false;
    }

    const experiment = getExperiment(EXPERIMENTS.forgotButtonPosition.name);

    if (!isExperimentRecognized(experiment)) {
      return false;
    }

    const paramValue = experiment.get(EXPERIMENTS.forgotButtonPosition.parameters.showProminentButton, false);

    return paramValue as boolean;
  }, [client, isLoading, getExperiment]);

  return {
    showProminentButton,
    isLoading,
  };
}

/**
 * Hook for Improved Promotion Wordings experiment
 * Returns a function to get the appropriate translation key based on experiment flag
 */
export function useImprovedPromotionWordings() {
  const experiment = useExperiment(EXPERIMENTS.improvedPromotionWordings.name);

  const useImprovedWordings = experiment.get(
    EXPERIMENTS.improvedPromotionWordings.parameters.useImprovedWordings,
    false,
  );

  const getPromotionWording = useCallback(
    (baseKey: string): string => {
      return useImprovedWordings ? baseKey : `${baseKey}_old`;
    },
    [useImprovedWordings],
  );

  return {
    useImprovedWordings,
    getPromotionWording,
    experimentName: EXPERIMENTS.improvedPromotionWordings.name,
  };
}
